/**
 * @Author: huangxuejuan
 * @Date: 2025-05-09
 * @Description: 智齿客服核心服务，管理转人工、消息收发、超时控制
 */
import { useConversationStore } from "@/store/conversation"
import {
  sobotTransferToHuman,
  sobotSendMessage,
  sobotGetMessages,
  sobotEndSession,
  type SobotMessageItem,
  type SobotSendMessageResponse
} from "@/api/conversation"
import type { ChatItem, MessageFile } from "@/types/app"
import { ElMessageBox, ElMessage } from "element-plus"
import { getEnv } from "@/assets/js/utils/env"

// 人工会话超时时间（分钟）
const HUMAN_TIMEOUT_MINUTES = 3
// 转人工轮询间隔（毫秒）
const TRANSFER_POLL_INTERVAL = 3000
// 消息轮询间隔（毫秒）
const MESSAGE_POLL_INTERVAL = 3000
// 消息轮询连续失败上限,超过则自动退出人工
const MAX_MESSAGE_POLL_ERRORS = 3

// 模块级会话状态: 多个 useSobotService() 实例需共享同一份定时器/排队消息ID,
// 否则 conversationService 与 AgentChatInputBox 各自的闭包互相看不到对方启动的轮询,
// 一边 startTransferPolling 一边 stopTransferPolling 会无效
let transferPollTimer: number | null = null
let messagePollTimer: number | null = null
let idleTimer: number | null = null
let lastMessageTime = 0
// 排队/待分配状态用系统消息展示, 跟踪当前消息 ID 以便后续更新或移除
let queueSystemMsgId: string | null = null
// 轮询代际计数: 每次 start* 自增, 旧闭包 await 返回后通过对比 epoch 判断自己是否已被作废,
// 防止旧闭包 setTimeout 复活 / addHumanMessage 越权写入(stop 之后又触底导致消息泄露)
let transferPollEpoch = 0
let messagePollEpoch = 0

export function useSobotService() {
  const store = useConversationStore()

  /**
   * 新增或更新排队系统消息, 接入成功/失败时需调用 removeQueueSystemMessage 移除
   */
  function addOrUpdateQueueMessage(content: string) {
    if (queueSystemMsgId) {
      const idx = store.chatList.findIndex((item) => item.id === queueSystemMsgId)
      if (idx !== -1) {
        // 已存在则就地更新内容, 避免多条排队气泡堆积
        const updated = [...store.chatList]
        updated[idx] = { ...updated[idx], content }
        store.setChatList(updated)
        return
      }
      // ID 已失效(可能被外部清空), 落到下方重建
    }
    const id = `system-queue-${Date.now()}`
    queueSystemMsgId = id
    const systemMsg: ChatItem = {
      id,
      content,
      isAnswer: true,
      msgSource: "system",
      isDivider: true
    }
    store.setChatList([...store.chatList, systemMsg])
  }

  /**
   * 移除排队系统消息(进入人工客服或转人工失败时调用)
   */
  function removeQueueSystemMessage() {
    if (!queueSystemMsgId) return
    store.setChatList(store.chatList.filter((item) => item.id !== queueSystemMsgId))
    queueSystemMsgId = null
  }

  /**
   * 开始转人工轮询
   * status 状态码说明:
   *   -1 重复请求拒绝(继续轮询)
   *    0 排队中(继续轮询)
   *    1 成功分配客服
   *    2 无客服在线(失败退出)
   *    3 用户在黑名单(失败退出)
   *    4 待分配-异步模式(继续轮询)
   *    6 需要选择技能组(失败退出,暂未支持技能组选择)
   */
  async function startTransferPolling(conversationId: string, busiAgentId: string) {
    if (transferPollTimer) {
      clearTimeout(transferPollTimer)
      transferPollTimer = null
    }
    // 本轮 epoch: 后续每次 await 返回都校验, 若已被 stop/重启则静默退出
    const epoch = ++transferPollEpoch

    // 立即置为人工模式, 避免接口返回前 UI 出现"新对话按钮先显示再隐藏"的闪烁
    // 接口最终失败/异常时再回退
    store.setHumanService(true)
    // 首次轮询前以系统消息形式给出等待提示, 后续状态变化时就地更新
    addOrUpdateQueueMessage("正在为您接入人工客服...")

    // 本轮转人工是否已展示过"客服繁忙"人工消息, 排队首次出现时仅提示一次
    let hasShownAgentBusyMessage = false
    // 是否已展示过"重复请求"Toast, 避免轮询期间反复弹出
    let hasShownDuplicateToast = false
    // 是否已展示过"待分配"气泡, 避免轮询期间反复气泡堆积
    let hasShownAllocatedMessage = false

    // 转人工失败处理: 移除排队消息, 回退人工模式并提示用户
    const handleTransferFailure = (tip: string) => {
      removeQueueSystemMessage()
      store.setHumanService(false)
      ElMessage.warning(tip)
    }

    const poll = async () => {
      if (epoch !== transferPollEpoch) return
      try {
        const res = await sobotTransferToHuman({
          conversation_id: conversationId,
          busi_agent_id: busiAgentId
        })
        // 旧闭包返回时若已被作废(用户已退出/重启轮询), 静默丢弃响应避免写脏聊天
        if (epoch !== transferPollEpoch) return

        const item = res.sobotResponse?.item
        const status = item?.status ?? res.sobotResponse?.status

        switch (status) {
          case -1: {
            // 重复请求拒绝,先前请求仍在处理中,仅 Toast 提示一次后继续轮询
            if (!hasShownDuplicateToast) {
              ElMessage.warning("您已发起转人工请求，请勿重复操作")
              hasShownDuplicateToast = true
            }
            transferPollTimer = window.setTimeout(poll, TRANSFER_POLL_INTERVAL)
            return
          }
          case 0: {
            // 排队中: 首次进入时先以人工消息提示客服繁忙, 再展示排队信息
            if (!hasShownAgentBusyMessage) {
              // 先移除"正在为您接入..."占位, 保证消息顺序: 人工提示 -> 排队信息
              removeQueueSystemMessage()
              addHumanMessage({
                id: Date.now(),
                messageId: `human-queue-busy-${Date.now()}`,
                content: "目前客服均在接待中，请稍后"
              } as SobotMessageItem)
              hasShownAgentBusyMessage = true
            }
            const queueInfo = item?.count != null ? `排队人数：${item.count}人` : "排队中..."
            addOrUpdateQueueMessage(queueInfo)
            transferPollTimer = window.setTimeout(poll, TRANSFER_POLL_INTERVAL)
            return
          }
          case 1: {
            // 成功分配客服: 先移除排队消息, 再添加进入人工的分割线消息
            removeQueueSystemMessage()
            store.setHumanService(true)
            addSystemMessage(`客服${item?.agent_name}为您提供服务`, "enter")
            // 用人工客服气泡形式展示客服欢迎语
            const welcomeMsg = "你已进入人工客服，很高兴为您服务。"
            addHumanMessage({
              id: Date.now(),
              messageId: `human-welcome-${Date.now()}`,
              content: welcomeMsg,
              agentNick: item?.agent_name,
              agentFace: item?.agent_face
            } as SobotMessageItem)
            // 开始消息轮询（使用 cid）
            const cid = item?.cid || ""
            if (cid) {
              startMessagePolling(cid)
            }
            // 启动空闲超时
            startIdleTimeout()
            return
          }
          case 2: {
            // 无客服在线: 以人工气泡展示提示语, 同时退出人工模式让用户回到 AI 对话
            removeQueueSystemMessage()
            store.setHumanService(false)
            addHumanMessage({
              id: Date.now(),
              messageId: `human-no-agent-${Date.now()}`,
              content: "您好，当前没有在线的客服，人工客服服务时间：8:30-21:30。"
            } as SobotMessageItem)
            return
          }
          case 3: {
            // 用户在黑名单
            handleTransferFailure("暂无法为您提供人工客服服务")
            return
          }
          case 4: {
            // 待分配(异步模式),首次以人工气泡提示, 后续继续轮询等待客服分配
            if (!hasShownAllocatedMessage) {
              removeQueueSystemMessage()
              addHumanMessage({
                id: Date.now(),
                messageId: `human-allocated-${Date.now()}`,
                content: "已收到您的人工咨询请求，匹配到客服后将自动为您接入"
              } as SobotMessageItem)
              hasShownAllocatedMessage = true
            }
            transferPollTimer = window.setTimeout(poll, TRANSFER_POLL_INTERVAL)
            return
          }
          default: {
            // 未知状态: 回退人工模式
            handleTransferFailure("转人工客服失败，请稍后重试")
            return
          }
        }
      } catch (error: any) {
        if (epoch !== transferPollEpoch) return
        console.error("转人工轮询失败:", error)
        // 异常: 移除排队消息, 回退人工模式
        removeQueueSystemMessage()
        store.setHumanService(false)
        const msg = typeof error === "string" ? error : error?.message || "转人工客服请求失败"
        ElMessage.error(msg)
        return
      }
    }

    // 立即执行第一次
    poll()
  }

  /**
   * 停止转人工轮询
   */
  function stopTransferPolling() {
    // 自增 epoch 使在途的旧闭包 await 返回后被作废
    transferPollEpoch++
    if (transferPollTimer) {
      clearTimeout(transferPollTimer)
      transferPollTimer = null
    }
  }

  /**
   * 开始消息轮询
   */
  function startMessagePolling(cid: string) {
    if (messagePollTimer) {
      clearTimeout(messagePollTimer)
      messagePollTimer = null
    }
    // 本轮 epoch: 防止 stop 后在途 await 返回时 addHumanMessage 越权写入
    const epoch = ++messagePollEpoch

    // 最新人工回答的 id，作为下次轮询的 msgid;首次拉取不上送
    let lastMsgId: number | undefined
    // 连续失败计数: 用于触发自动退出人工
    let consecutiveErrors = 0

    const poll = async () => {
      if (epoch !== messagePollEpoch) return
      if (!store.isHumanService) return
      try {
        const messages = await sobotGetMessages(cid, lastMsgId)
        // 旧闭包 await 返回时若已被作废(stop/退出/切会话), 静默丢弃避免污染聊天
        if (epoch !== messagePollEpoch) return
        if (!store.isHumanService) return
        // 请求成功,重置连续失败计数
        consecutiveErrors = 0
        if (messages && messages.length > 0) {
          for (const msg of messages) {
            // 客服主动结束会话, 主动退出人工
            if (msg.status == "1" || msg.status == "2") {
              doExitHuman()
              // 更新 lastMsgId 并终止后续消息处理
              if (typeof msg.id === "number" && (lastMsgId == null || msg.id > lastMsgId)) {
                lastMsgId = msg.id
              }
              return
            }
            addHumanMessage(msg)
            // 更新最新人工回答的 id
            if (typeof msg.id === "number" && (lastMsgId == null || msg.id > lastMsgId)) {
              lastMsgId = msg.id
            }
          }
        }
      } catch (error) {
        if (epoch !== messagePollEpoch) return
        console.error("获取客服消息失败:", error)
        consecutiveErrors++
        // 连续失败达到上限, 自动退出人工并提示用户
        if (consecutiveErrors >= MAX_MESSAGE_POLL_ERRORS) {
          ElMessage.error("人工客服连接异常,已退出人工")
          doExitHuman()
          return
        }
      }

      if (epoch === messagePollEpoch && store.isHumanService) {
        messagePollTimer = window.setTimeout(poll, MESSAGE_POLL_INTERVAL)
      }
    }

    poll()
  }

  /**
   * 停止消息轮询
   */
  function stopMessagePolling() {
    // 自增 epoch 使在途的旧闭包 await 返回后被作废
    messagePollEpoch++
    if (messagePollTimer) {
      clearTimeout(messagePollTimer)
      messagePollTimer = null
    }
  }

  /**
   * 添加系统消息到聊天列表
   */
  function addSystemMessage(content: string, type: "enter" | "exit" | "timeout") {
    const systemMsg: ChatItem = {
      id: `system-${type}-${Date.now()}`,
      content,
      isAnswer: true,
      msgSource: "system",
      isDivider: type === "enter" || type === "exit"
    }
    store.setChatList([...store.chatList, systemMsg])
  }

  /**
   * 添加人工客服消息到聊天列表
   * 图片/文件统一放在 message_files 中,与 AI 客服逻辑保持一致
   */
  function addHumanMessage(msg: SobotMessageItem) {
    // 优先使用 messageId(字符串),其次使用 id(数字)
    const msgId = msg.messageId || (msg.id != null ? String(msg.id) : "")
    if (!msgId) return

    const exists = store.chatList.find((item) => item.messageId === msgId)
    if (exists) return

    // 从 URL 中提取文件名: 先剥掉 query/hash 再取最后一段, 避免 "image.jpg?token=xxx" 被当成完整文件名
    const extractFileName = (url: string, fallback: string): string => {
      const cleaned = url.split(/[?#]/)[0]
      return cleaned.split("/").pop() || fallback
    }

    const messageFiles: MessageFile[] = []

    if (msg.msgType === "IMAGE" && msg.content) {
      const url = msg.content
      // 智齿客服 IMAGE 类型: content 即为图片 URL
      const fileName = extractFileName(url, "image")
      messageFiles.push({
        file_id: msgId,
        file_name: fileName,
        file_url: url,
        file_size: 0
      })
    } else if (msg.msgType === "FILE" && msg.content) {
      const url = msg.content
      // 智齿客服 FILE 类型: content 为文件 URL,rawContent.msg 为文件名
      let fileName = ""
      if (msg.rawContent) {
        try {
          const raw = JSON.parse(msg.rawContent)
          if (raw.msg) fileName = raw.msg
        } catch {
          /* ignore */
        }
      }
      if (!fileName) {
        fileName = extractFileName(url, "未知文件")
      }
      messageFiles.push({
        file_id: msgId,
        file_name: fileName,
        file_url: url,
        file_size: 0
      })
    }
    // IMAGE/FILE 类型的消息文本由 message_files 渲染,content 置空避免重复显示
    const chatContent = msg.msgType === "IMAGE" || msg.msgType === "FILE" ? "" : msg.content
    // content 与 message_files 均为空, 跳过空气泡
    if (!chatContent && messageFiles.length === 0) return

    const chatItem: ChatItem = {
      id: msgId,
      messageId: msgId,
      content: chatContent,
      isAnswer: true,
      msgSource: "human",
      message_files: messageFiles,
      isCompleted: true
    }
    store.setChatList([...store.chatList, chatItem])
  }

  /**
   * 用户发送消息到智齿客服
   * @returns {Promise<{ success: boolean; errorMsg?: string }>} 发送结果
   */
  async function sendHumanMessage(
    conversationId: string,
    message: string,
    fileIds: string[] = []
  ): Promise<{ success: boolean; errorMsg?: string }> {
    if (!message.trim() && fileIds.length === 0) return { success: false }
    try {
      const busi_agent_id = sessionStorage.getItem("busi_agent_id") || ""
      const res: SobotSendMessageResponse = await sobotSendMessage({
        conversation_id: conversationId,
        message,
        busi_agent_id,
        file_ids: fileIds.length > 0 ? fileIds : undefined
      })
      // 检查智齿返回的状态码
      if (res && res.status && res.status !== "1") {
        const errorMsg = res.ret_msg || "消息发送失败，请重试"
        // status 为 2 时，会话可能已结束，自动退出人工客服
        if (res.status === "2") {
          doExitHuman()
          ElMessage.error("人工客服会话已结束")
          return { success: false, errorMsg: "人工客服会话已结束" }
        }
        ElMessage.error(errorMsg)
        return { success: false, errorMsg }
      }
      // 重置空闲超时
      resetIdleTimeout()
      return { success: true }
    } catch (error) {
      console.error("发送人工客服消息失败:", error)
      ElMessage.error("消息发送失败，请重试")
      return { success: false, errorMsg: "消息发送失败，请重试" }
    }
  }

  /**
   * 退出人工客服
   */
  async function exitHumanService(conversationId: string) {
    try {
      await sobotEndSession({ conversation_id: conversationId })
    } catch (error) {
      console.error("结束人工会话失败:", error)
    }
    doExitHuman()
  }

  /**
   * 内部退出处理
   */
  function doExitHuman() {
    stopTransferPolling()
    stopMessagePolling()
    stopIdleTimeout()
    // 排队中退出时, 清理可能残留的排队系统消息
    removeQueueSystemMessage()
    store.clearHumanService()
    addHumanMessage({
      id: Date.now(),
      messageId: `human-welcome-${Date.now()}`,
      content: "若您仍有疑问，欢迎再次咨询。"
    } as SobotMessageItem)
    addSystemMessage("已退出人工客服", "exit")
  }

  /**
   * 启动空闲超时
   */
  function startIdleTimeout() {
    stopIdleTimeout()
    lastMessageTime = Date.now()
    const timeoutMs = HUMAN_TIMEOUT_MINUTES * 60 * 1000

    const check = () => {
      const elapsed = Date.now() - lastMessageTime
      if (elapsed >= timeoutMs) {
        // 超时,自动结束(exitHumanService 内部已 try/catch, 不会抛出, 无需再 .catch)
        if (store.isHumanService) {
          exitHumanService(store.currentConversationId)
        }
        return
      }
      idleTimer = window.setTimeout(check, timeoutMs - elapsed)
    }

    idleTimer = window.setTimeout(check, timeoutMs)
  }

  /**
   * 重置空闲超时
   */
  function resetIdleTimeout() {
    if (store.isHumanService) {
      startIdleTimeout()
    }
  }

  /**
   * 停止空闲超时
   */
  function stopIdleTimeout() {
    if (idleTimer) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }

  /**
   * 显示退出人工确认弹窗
   * 移动端使用更适合触摸操作的样式(更大圆角、居中、无关闭按钮), PC 端保持默认
   */
  function confirmExitHuman(conversationId: string): Promise<boolean> {
    const { isMobile } = getEnv()
    const options: Parameters<typeof ElMessageBox.confirm>[2] = {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning"
    }
    if (isMobile) {
      Object.assign(options, {
        customClass: "custom-confirm-mobile",
        showClose: false,
        closeOnClickModal: true
      })
    }
    return new Promise((resolve) => {
      ElMessageBox.confirm("确定要退出人工吗？", "提示", options)
        .then(() => {
          const exitUserMsg: ChatItem = {
            id: `user-exit-human-${Date.now()}`,
            content: "退出人工",
            isAnswer: false
          }
          store.setChatList([...store.chatList, exitUserMsg])
          exitHumanService(conversationId)
          resolve(true)
        })
        .catch(() => {
          resolve(false)
        })
    })
  }

  /**
   * 静默停止所有人工客服活动: 用于切换会话/卸载场景, 不调用结束接口、不写系统消息,
   * 仅清理本地轮询和状态, 避免旧会话的轮询响应污染新会话聊天列表
   */
  function stopAllSobotActivity() {
    stopTransferPolling()
    stopMessagePolling()
    stopIdleTimeout()
    removeQueueSystemMessage()
    if (store.isHumanService) {
      store.clearHumanService()
    }
  }

  return {
    startTransferPolling,
    stopTransferPolling,
    startMessagePolling,
    stopMessagePolling,
    sendHumanMessage,
    exitHumanService,
    confirmExitHuman,
    addSystemMessage,
    addHumanMessage,
    resetIdleTimeout,
    startIdleTimeout,
    stopIdleTimeout,
    stopAllSobotActivity
  }
}
